import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';

import firebase from '../../config/firebase.config';
import {
  addNotification,
  notificationStates,
} from '../../components/Notification';
import styles from './Settings.module.scss';
import { textVariantsEnum, fontWeightEnum, Text } from '@/components/Text';
import { buttonVariants, Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Checkbox } from '@/components/Checkbox';
import { Modal } from '@/components/Modal';
import { PolicyConfigurationModal } from './PolicyConfigurationModal.componnent';
import { StealthModeModal } from './StealthModeModal.component';
import { featureIds, useFeatures } from '@/hooks/useFeatures';

export const SettingsComponent = () => {
  const { hasAccess } = useFeatures();
  const inputsDisabledForStarterAndGrowPlan = !hasAccess.feature(
    featureIds.SETTINGS_PAGE_INPUTS
  );
  const database = firebase.firestore();

  const userProfile = useSelector((state) => state.auth.profile);

  // Check if user is admin
  const isAdmin = userProfile?.activeCompany?.role === 'Admin';

  // Leave Policy state
  const [leavePolicyModal, setLeavePolicyModal] = useState(false);
  const [paidLeaveAllocation, setPaidLeaveAllocation] = useState('');
  const [currentLeavePolicy, setCurrentLeavePolicy] = useState(null);
  const [leavePolicyLoading, setLeavePolicyLoading] = useState(false);

  // Alert Configuration state
  const [alertConfig, setAlertConfig] = useState({
    enabled: false,
    productivityPercentageThreshold: 50,
    lateLoginAlertMinutes: 30,
  });
  const [originalAlertConfig, setOriginalAlertConfig] = useState({
    enabled: false,
    productivityPercentageThreshold: 50,
    lateLoginAlertMinutes: 30,
  });
  const [alertConfigLoading, setAlertConfigLoading] = useState(false);

  // Leave Policy functions
  const toggleLeavePolicy = () => {
    // Security check: Only admins can access leave policy configuration
    if (!isAdmin) {
      addNotification({
        message:
          'Access denied: Only administrators can configure leave policy',
        level: notificationStates.error,
      });
      return;
    }
    setLeavePolicyModal(!leavePolicyModal);
  };

  const handleModalClosed = () => {
    setPaidLeaveAllocation('');
  };

  const getCurrentLeavePolicy = async () => {
    if (!userProfile?.activeCompany?.companyId) return;

    try {
      const currentYear = new Date().getFullYear();
      const doc = await database
        .collection('companyDatabase')
        .doc(userProfile.activeCompany.companyId)
        .collection('leavePolicy')
        .doc(currentYear.toString())
        .get();

      if (doc.exists) {
        const policy = doc.data();
        setCurrentLeavePolicy(policy);
        setPaidLeaveAllocation(policy.paidLeaveAllocation || '');
      }
    } catch (error) {
      console.error('Error fetching leave policy:', error);
    }
  };

  const handleLeavePolicySubmit = async (e) => {
    e.preventDefault();

    // Security check: Only admins can configure leave policy
    if (!isAdmin) {
      addNotification({
        message:
          'Access denied: Only administrators can configure leave policy',
        level: notificationStates.error,
      });
      return;
    }

    if (!paidLeaveAllocation || paidLeaveAllocation < 0) {
      addNotification({
        message: 'Please enter a valid number of paid leave days',
        level: notificationStates.error,
      });
      return;
    }

    setLeavePolicyLoading(true);

    try {
      const currentYear = new Date().getFullYear();
      const docRef = database
        .collection('companyDatabase')
        .doc(userProfile.activeCompany.companyId)
        .collection('leavePolicy')
        .doc(currentYear.toString());

      // Check if document exists to determine if we're creating or updating
      const doc = await docRef.get();
      const isNewDocument = !doc.exists;

      const policyData = {
        year: currentYear,
        paidLeaveAllocation: parseInt(paidLeaveAllocation),
        updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        updatedBy: userProfile.id,
        companyId: userProfile.activeCompany.companyId,
      };

      // Only set createdAt and createdBy for new documents
      if (isNewDocument) {
        policyData.createdAt = firebase.firestore.FieldValue.serverTimestamp();
        policyData.createdBy = userProfile.id;
      }

      await docRef.set(policyData, { merge: true });

      // Refetch the data to get the actual timestamp
      await getCurrentLeavePolicy();

      toggleLeavePolicy();

      addNotification({
        message: 'Leave policy updated successfully',
        level: notificationStates.success,
      });
    } catch (error) {
      console.error('Error updating leave policy:', error);
      addNotification({
        message: 'Failed to update leave policy',
        level: notificationStates.error,
      });
    } finally {
      setLeavePolicyLoading(false);
    }
  };

  // Fetch alert configuration
  const getAlertConfig = async () => {
    if (!userProfile?.activeCompany?.companyId) return;

    try {
      const doc = await database
        .collection('companyDatabase')
        .doc(userProfile.activeCompany.companyId)
        .get();

      if (doc.exists) {
        const data = doc.data();
        if (data.alertConfig) {
          const config = {
            enabled: data.alertConfig.enabled || false,
            productivityPercentageThreshold:
              data.alertConfig.productivityPercentageThreshold || 50,
            lateLoginAlertMinutes: data.alertConfig.lateLoginAlertMinutes || 30,
          };
          setAlertConfig(config);
          setOriginalAlertConfig(config);
        }
      }
    } catch (error) {
      console.error('Error fetching alert config:', error);
    }
  };

  // Check if alert config has changed
  const hasAlertConfigChanged = () => {
    // Convert to numbers for proper comparison
    const currentProd =
      parseFloat(alertConfig.productivityPercentageThreshold) || 0;
    const originalProd =
      parseFloat(originalAlertConfig.productivityPercentageThreshold) || 0;
    const currentLate = parseInt(alertConfig.lateLoginAlertMinutes) || 0;
    const originalLate =
      parseInt(originalAlertConfig.lateLoginAlertMinutes) || 0;

    return (
      alertConfig.enabled !== originalAlertConfig.enabled ||
      currentProd !== originalProd ||
      currentLate !== originalLate
    );
  };

  const handleAlertConfigChange = (field, value) => {
    setAlertConfig((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAlertConfigSubmit = async (e) => {
    e.preventDefault();

    // Security check: Only admins can configure alerts
    if (!isAdmin) {
      addNotification({
        message: 'Access denied: Only administrators can configure alerts',
        level: notificationStates.error,
      });
      return;
    }

    // Validation
    if (alertConfig.enabled) {
      if (
        !alertConfig.productivityPercentageThreshold ||
        alertConfig.productivityPercentageThreshold < 0 ||
        alertConfig.productivityPercentageThreshold > 100
      ) {
        addNotification({
          message: 'Please enter a valid productivity percentage (0-100)',
          level: notificationStates.error,
        });
        return;
      }
      if (
        !alertConfig.lateLoginAlertMinutes ||
        alertConfig.lateLoginAlertMinutes < 1
      ) {
        addNotification({
          message:
            'Please enter a valid late login alert time (minimum 1 minute)',
          level: notificationStates.error,
        });
        return;
      }
    }

    setAlertConfigLoading(true);

    try {
      await database
        .collection('companyDatabase')
        .doc(userProfile.activeCompany.companyId)
        .set(
          {
            alertConfig: {
              enabled: alertConfig.enabled,
              productivityPercentageThreshold: alertConfig.enabled
                ? parseFloat(alertConfig.productivityPercentageThreshold)
                : alertConfig.productivityPercentageThreshold,
              lateLoginAlertMinutes: alertConfig.enabled
                ? parseInt(alertConfig.lateLoginAlertMinutes)
                : alertConfig.lateLoginAlertMinutes,
              updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
              updatedBy: userProfile.id,
            },
          },
          { merge: true }
        );

      // Update original config after successful save
      setOriginalAlertConfig({
        enabled: alertConfig.enabled,
        productivityPercentageThreshold: alertConfig.enabled
          ? parseFloat(alertConfig.productivityPercentageThreshold)
          : alertConfig.productivityPercentageThreshold,
        lateLoginAlertMinutes: alertConfig.enabled
          ? parseInt(alertConfig.lateLoginAlertMinutes)
          : alertConfig.lateLoginAlertMinutes,
      });

      addNotification({
        message: 'Alert configuration saved successfully',
        level: notificationStates.success,
      });
    } catch (error) {
      console.error('Error saving alert config:', error);
      addNotification({
        message: 'Failed to save alert configuration',
        level: notificationStates.error,
      });
    } finally {
      setAlertConfigLoading(false);
    }
  };

  useEffect(() => {
    if (userProfile?.activeCompany !== undefined) {
      getCurrentLeavePolicy();
      getAlertConfig();
    }
  }, [userProfile?.activeCompany]);

  const [checked, setChecked] = useState();
  useEffect(() => {
    database
      .collection('companyDatabase')
      .doc(userProfile?.activeCompany?.companyId || 'no id')
      .get()
      .then((doc) => {
        if (doc.exists) {
          let jsonData = doc.data();
          setChecked(jsonData.screenshot);
        } else {
          console.log('No such document!');
        }
      })
      .catch((error) => {
        console.log('Error getting document:', error);
      });
  }, []);

  const [disabled, setDisabled] = useState(false);

  const handleScreenShots = (e) => {
    setDisabled(true);
    const newValue = !checked;
    setChecked(newValue);
    let newData = {
      screenshot: newValue,
    };
    database
      .collection('companyDatabase')
      .doc(userProfile?.activeCompany.companyId)
      .set(newData, { merge: true })
      .then(() => {
        return database
          .collection('companyDatabase')
          .doc(userProfile?.activeCompany.companyId)
          .get();
      })
      .then((doc) => {
        if (doc.exists) {
          console.log('Updated screenShot value:', doc.data().screenshot);
          setDisabled(false);
        } else {
          console.log('Document does not exist');
        }
      })
      .catch((error) => {
        console.error('Error updating document:', error);
      });
  };

  // Stealth Mode state
  const [stealthMode, setStealthMode] = useState(false);
  const [stealthModeDisabled, setStealthModeDisabled] = useState(false);
  const [stealthModeConfirmModal, setStealthModeConfirmModal] = useState(false);
  const [pendingStealthModeValue, setPendingStealthModeValue] = useState(null);

  // Fetch stealth mode on mount and when company changes
  useEffect(() => {
    if (!userProfile?.activeCompany?.companyId) return;

    const companyRef = database
      .collection('companyDatabase')
      .doc(userProfile.activeCompany.companyId);

    const unsubscribe = companyRef.onSnapshot(
      (doc) => {
        if (doc.exists) {
          const data = doc.data();
          // Default to false if field doesn't exist
          setStealthMode(data.stealthMode === true);
          // Update localStorage cache
          const cacheKey = `stealthMode_${userProfile.activeCompany.companyId}`;
          localStorage.setItem(cacheKey, String(data.stealthMode === true));
        } else {
          setStealthMode(false);
          const cacheKey = `stealthMode_${userProfile.activeCompany.companyId}`;
          localStorage.setItem(cacheKey, 'false');
        }
      },
      (error) => {
        console.error('Error fetching stealthMode:', error);
      }
    );

    return () => unsubscribe();
  }, [userProfile?.activeCompany?.companyId]);

  const handleStealthModeChange = (e) => {
    // Security check: Only admins can change stealth mode
    if (!isAdmin) {
      addNotification({
        message:
          'Access denied: Only administrators can configure stealth mode',
        level: notificationStates.error,
      });
      return;
    }

    const newValue = !stealthMode;
    setPendingStealthModeValue(newValue);
    setStealthModeConfirmModal(true);
  };

  const handleStealthModeConfirm = async () => {
    if (!userProfile?.activeCompany?.companyId) return;

    setStealthModeDisabled(true);
    const newValue = pendingStealthModeValue;

    try {
      await database
        .collection('companyDatabase')
        .doc(userProfile.activeCompany.companyId)
        .set(
          {
            stealthMode: newValue,
          },
          { merge: true }
        );

      // Update localStorage cache
      const cacheKey = `stealthMode_${userProfile.activeCompany.companyId}`;
      localStorage.setItem(cacheKey, String(newValue));

      setStealthMode(newValue);
      setStealthModeConfirmModal(false);
      setPendingStealthModeValue(null);

      addNotification({
        message: `Stealth mode ${
          newValue ? 'enabled' : 'disabled'
        } successfully`,
        level: notificationStates.success,
      });
    } catch (error) {
      console.error('Error updating stealthMode:', error);
      addNotification({
        message: 'Failed to update stealth mode',
        level: notificationStates.error,
      });
    } finally {
      setStealthModeDisabled(false);
    }
  };

  const handleStealthModeCancel = () => {
    setStealthModeConfirmModal(false);
    setPendingStealthModeValue(null);
  };
  const getRecentDateUpdated = () => {
    if (
      currentLeavePolicy?.updatedAt &&
      typeof currentLeavePolicy.updatedAt.toDate === 'function'
    ) {
      return new Date(
        currentLeavePolicy.updatedAt.toDate()
      ).toLocaleDateString();
    } else {
      return 'Recently';
    }
  };
  return (
    <>
      <div className={styles.settingsPage}>
        <div className={styles.pageHeader}>
          <Text
            variant={textVariantsEnum.Header}
            fontWeight={fontWeightEnum.Normal}
          >
            Manage Settings
          </Text>
        </div>
        <div className={styles.settingsContent}>
          <div className={styles.checkboxContainer}>
            <Checkbox
              text={'Enable Screenshots'}
              checked={checked}
              disabled={disabled || inputsDisabledForStarterAndGrowPlan}
              id={'screenshot'}
              onClick={handleScreenShots}
            />
            {isAdmin && (
              <Checkbox
                id={'stealthMode'}
                text={'Enable Stealth Mode'}
                checked={stealthMode}
                disabled={
                  stealthModeDisabled || inputsDisabledForStarterAndGrowPlan
                }
                onClick={handleStealthModeChange}
              />
            )}
          </div>
          <div className={styles.leavePolicyContainer}>
            <Text
              variant={textVariantsEnum.Text_sm}
              fontWeight={fontWeightEnum.Medium}
            >
              Leave Policy
            </Text>
            <Text
              variant={textVariantsEnum.Text_13}
              fontWeight={fontWeightEnum.Normal}
              color={'gray-600'}
            >
              {isAdmin
                ? 'Configure annual leave allocations for employees'
                : 'Current leave policy for this year'}
            </Text>
            <div className={styles.leavePolicyDetailsContainer}>
              <div className={styles.leavePoilcyDetails}>
                {currentLeavePolicy ? (
                  <>
                    <Text
                      variant={textVariantsEnum.Text_sm}
                      fontWeight={fontWeightEnum.Normal}
                    >
                      Current Leave Policy ({currentLeavePolicy?.year})
                    </Text>
                    <div className={styles.currentLeaveTitle}>
                      <Text
                        variant={textVariantsEnum.Text_sm}
                        fontWeight={fontWeightEnum.Medium}
                      >
                        Paid Leave Days :{' '}
                        {currentLeavePolicy.paidLeaveAllocation} days per year
                      </Text>
                      <Text
                        variant={textVariantsEnum.Text_xs}
                        fontWeight={fontWeightEnum.Normal}
                        color={'gray-600'}
                      >
                        Last updated:{getRecentDateUpdated()}
                      </Text>
                    </div>
                  </>
                ) : (
                  <>
                    <Text
                      variant={textVariantsEnum.Text_sm}
                      fontWeight={fontWeightEnum.Normal}
                    >
                      No leave policy configured yet
                    </Text>
                    <Text
                      variant={textVariantsEnum.Text_sm}
                      fontWeight={fontWeightEnum.Normal}
                    >
                      {isAdmin
                        ? 'Set up annual leave allocations to enable leave balance tracking.'
                        : 'Contact your administrator to configure leave policy.'}
                    </Text>
                  </>
                )}
              </div>
              {isAdmin && (
                <Button
                  text={
                    currentLeavePolicy ? 'Update Policy' : 'Configure Policy'
                  }
                  className={styles.updatePolicyButton}
                  variant={buttonVariants.primary}
                  onClick={toggleLeavePolicy}
                  disabled={inputsDisabledForStarterAndGrowPlan}
                />
              )}
            </div>
          </div>

          <div className={styles.alertConfigurationContainer}>
            <div className={styles.alertConfigurationTitle}>
              <Text
                variant={textVariantsEnum.Text_sm}
                fontWeight={fontWeightEnum.Medium}
              >
                Alert Configuration
              </Text>
              <Text
                variant={textVariantsEnum.Text_13}
                fontWeight={fontWeightEnum.Normal}
                color={'gray-600'}
              >
                {isAdmin
                  ? 'Configure alerts for productivity and late login notifications'
                  : 'Current alert settings for your company'}
              </Text>
            </div>
            {isAdmin ? (
              <div className={styles.alertConfigurationAdminForm}>
                <Checkbox
                  id={'enableAlerts'}
                  text={'Enable Alerts'}
                  checked={alertConfig.enabled}
                  onClick={() =>
                    handleAlertConfigChange('enabled', !alertConfig.enabled)
                  }
                  disabled={inputsDisabledForStarterAndGrowPlan}
                />
                <Text
                  variant={textVariantsEnum.Text_13}
                  fontWeight={fontWeightEnum.Normal}
                  color={'gray-600'}
                >
                  When enabled, alerts will be sent based on the configured
                  thresholds below
                </Text>
                {alertConfig.enabled && (
                  <div className={styles.alertConfigurationAdminFormInputs}>
                    <Input
                      labelConfig={{
                        variant: textVariantsEnum.Text_sm,
                        fontWeight: fontWeightEnum.Medium,
                      }}
                      label="Productivity Percentage Threshold"
                      value={alertConfig.productivityPercentageThreshold}
                      disabled={inputsDisabledForStarterAndGrowPlan}
                      onChange={(e) =>
                        handleAlertConfigChange(
                          'productivityPercentageThreshold',
                          e.target.value
                        )
                      }
                      placeholder="Enter percentage (0-100)"
                      min="0"
                      max="100"
                      step="0.1"
                    />
                    <Text
                      variant={textVariantsEnum.Text_13}
                      fontWeight={fontWeightEnum.Normal}
                      color={'gray-600'}
                    >
                      Alert will be sent if a user's productivity percentage
                      goes below this threshold
                    </Text>
                    {/* <Input
                      labelConfig={{
                        variant: textVariantsEnum.Text_sm,
                        fontWeight: fontWeightEnum.Medium,
                      }}
                      className={'mt-3'}
                      label="Late Login Alert Time (Minutes)"
                      value={alertConfig.lateLoginAlertMinutes}
                      onChange={(e) =>
                        handleAlertConfigChange(
                          'lateLoginAlertMinutes',
                          e.target.value
                        )
                      }
                      placeholder="Enter minutes (e.g., 30, 45, 60)"
                      min="1"
                    /> */}
                    {/* <Text
                      variant={textVariantsEnum.Text_13}
                      fontWeight={fontWeightEnum.Normal}
                      color={'gray-600'}
                    >
                      After the work start time, if a user hasn't logged in
                      within this many minutes, an alert will be sent
                    </Text> */}
                  </div>
                )}
                {hasAlertConfigChanged() && (
                  <Button
                    text={
                      alertConfigLoading
                        ? 'Saving...'
                        : 'Save Alert Configuration'
                    }
                    variant={buttonVariants.primary}
                    onClick={handleAlertConfigSubmit}
                    disabled={inputsDisabledForStarterAndGrowPlan}
                  />
                )}
              </div>
            ) : (
              <div className={styles.currentAlertConfigurationContainer}>
                {alertConfig.enabled ? (
                  <div className={styles.currentAlertConfiguration}>
                    <Text
                      variant={textVariantsEnum.Text_sm}
                      fontWeight={fontWeightEnum.Medium}
                    >
                      Current Alert Settings
                    </Text>
                    <div
                      className={styles.currentAlertConfigurationItemsContainer}
                    >
                      <div className={styles.currentAlertConfigurationRow}>
                        <div
                          className={styles.currentAlertConfigurationRowItem}
                        >
                          <Text
                            variant={textVariantsEnum.Text_sm}
                            fontWeight={fontWeightEnum.Medium}
                          >
                            Status:
                          </Text>
                          <Text
                            variant={textVariantsEnum.Text_sm}
                            fontWeight={fontWeightEnum.Medium}
                          >
                            Enabled
                          </Text>
                        </div>
                        <div
                          className={styles.currentAlertConfigurationRowItem}
                        >
                          <Text
                            variant={textVariantsEnum.Text_sm}
                            fontWeight={fontWeightEnum.Medium}
                          >
                            Productivity Percentage Threshold:
                          </Text>
                          <Text
                            variant={textVariantsEnum.Text_sm}
                            fontWeight={fontWeightEnum.Medium}
                          >
                            {alertConfig.productivityPercentageThreshold}%
                          </Text>
                        </div>
                      </div>
                      {/* <div className={styles.currentAlertConfigurationRow}>
                        <div
                          className={styles.currentAlertConfigurationRowItem}
                        >
                          <Text
                            variant={textVariantsEnum.Text_sm}
                            fontWeight={fontWeightEnum.Medium}
                          >
                            Late Login Alert Time (Minutes):
                          </Text>
                          <Text
                            variant={textVariantsEnum.Text_sm}
                            fontWeight={fontWeightEnum.Medium}
                          >
                            {alertConfig.lateLoginAlertMinutes} minutes
                          </Text>
                        </div>
                      </div> */}
                    </div>
                  </div>
                ) : (
                  <>
                    <i
                      className="ni ni-notification-70 text-muted"
                      style={{ fontSize: '48px', opacity: '0.5' }}
                    ></i>
                    <Text
                      variant={textVariantsEnum.Text_sm}
                      fontWeight={fontWeightEnum.Medium}
                    >
                      Alerts are currently disabled
                    </Text>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <Modal
        isVisible={leavePolicyModal}
        onClose={() => setLeavePolicyModal(false)}
      >
        <PolicyConfigurationModal
          isAdmin={isAdmin}
          onClose={() => setLeavePolicyModal(false)}
          getCurrentLeavePolicy={getCurrentLeavePolicy}
        />
      </Modal>
      <Modal
        isVisible={stealthModeConfirmModal}
        onClose={() => setStealthModeConfirmModal(false)}
      >
        <StealthModeModal
          pendingStealthModeValue={pendingStealthModeValue}
          onClose={() => setStealthModeConfirmModal(false)}
          setStealthMode={setStealthMode}
        />
      </Modal>
    </>
  );
};
